import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaPlug, FaSyncAlt, FaUsers, FaBuilding, FaCheckCircle, FaTimesCircle, FaExclamationTriangle } from 'react-icons/fa';
import { integrationsAPI } from '../services/api';
import { IntegrationCard } from '../components/AppIntegrationsEditor';

// Settings → Integration Hub (same idea as the LMS app's Integration page): one tab per
// connected app — AiventraLMS, AiventraDMS, AiventraQMS, SAP, other REST apps — with the
// connection, the calls each side makes, linked records and the sync history.
// "Purchased" stays a Commercial Setup decision; everything else is set here.

const fmt = (v) => (v ? new Date(v).toLocaleString() : '—');
const code = { background: 'var(--bg-color)', color: 'var(--text-color)', padding: '8px', borderRadius: '6px', fontFamily: 'monospace', fontSize: 'var(--fs-xs)', whiteSpace: 'pre-wrap', wordBreak: 'break-all', margin: '4px 0 0' };
const card = { border: '1px solid var(--border-color)', borderRadius: '8px', padding: '14px', background: 'var(--card-bg)' };
const muted = { fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)' };

const statusBadge = (item) => {
  const active = item.purchased && item.enabled;
  return <span className={`badge ${active ? 'badge-success' : item.purchased ? 'badge-warning' : 'badge-secondary'}`}>{active ? 'Active' : item.purchased ? 'Purchased — off' : 'Not purchased'}</span>;
};

const syncTone = (s) => (s === 'sent' || s === 'pushed' || s === 'success' ? 'badge-success' : s === 'failed' ? 'badge-danger' : s === 'partial' ? 'badge-warning' : 'badge-secondary');

const ACTION_LABELS = {
  test_connection: 'Connection test', employee_sync: 'Employee sync', department_sync: 'Department sync',
  products_pull: 'Product fetch', apis_pull: 'API fetch', document_effective: 'Protocol → LMS training',
  training_pull: 'Training completions pulled', completion_webhook: 'Completion received (webhook)',
  document_push: 'Document → DMS', deviation_push: 'Deviation → QMS', linked_sync: 'Linked sync run'
};

const summaryText = (summary) => (summary ? Object.entries(summary)
  .filter(([, v]) => v !== null && v !== undefined && typeof v !== 'object')
  .map(([k, v]) => `${k.replace(/_/g, ' ')}: ${v}`).join(', ') : '');

const SyncHistory = ({ integrationKey, refreshKey, limit = 50 }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    integrationsAPI.logs(integrationKey, limit)
      .then((res) => setRows(Array.isArray(res?.data?.data) ? res.data.data : []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [integrationKey, refreshKey, limit]);
  if (loading) return <div style={muted}>Loading sync history…</div>;
  if (!rows.length) return <div style={muted}>Nothing synced yet.</div>;
  return (
    <div className="table-responsive">
      <table className="table" style={{ fontSize: 'var(--fs-sm)' }}>
        <thead><tr><th>Date</th>{!integrationKey && <th>App</th>}<th>Action</th><th>Status</th><th>Details</th><th>By</th></tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.log_id}>
              <td style={{ whiteSpace: 'nowrap' }}>{fmt(r.created_at)}</td>
              {!integrationKey && <td>{r.integration_key}</td>}
              <td>{ACTION_LABELS[r.action] || r.action}</td>
              <td><span className={`badge ${syncTone(r.status)}`}>{r.status}</span></td>
              <td style={{ maxWidth: '420px' }}>{[summaryText(r.summary), r.message].filter(Boolean).join(' — ')}</td>
              <td>{r.triggered_by_name || (r.action === 'completion_webhook' ? 'LMS' : 'Automatic')}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const Endpoint = ({ title, children, note }) => (
  <div style={{ marginBottom: '10px' }}>
    <strong style={{ fontSize: 'var(--fs-sm)' }}>{title}</strong>
    <pre style={code}>{children}</pre>
    {note && <div style={{ ...muted, fontSize: 'var(--fs-xs)' }}>{note}</div>}
  </div>
);

const Steps = ({ items }) => (
  <ol style={{ margin: 0, paddingLeft: '18px', ...muted }}>
    {items.map((s) => <li key={s} style={{ marginBottom: '4px' }}>{s}</li>)}
  </ol>
);

const ActionButton = ({ label, icon, run, disabled, title }) => {
  const [busy, setBusy] = useState(false);
  const click = async () => {
    setBusy(true);
    try {
      const res = await run();
      toast.success(res?.data?.message || `${label} done`, { autoClose: 9000 });
    } catch (error) {
      toast.error(error?.response?.data?.message || `${label} failed`);
    } finally {
      setBusy(false);
    }
  };
  return (
    <button type="button" className="btn btn-outline" onClick={click} disabled={busy || disabled} title={title}>
      {icon} {busy ? 'Working…' : label}
    </button>
  );
};

// Effective protocols / reports and where each one is in the LMS and DMS.
const LinkedRecords = ({ refreshKey, onRan }) => {
  const [data, setData] = useState(null);
  const load = useCallback(() => {
    integrationsAPI.linkedStatus().then((res) => setData(res?.data?.data || null)).catch(() => setData({ documents: [], deviations: [] }));
  }, []);
  useEffect(() => { load(); }, [load, refreshKey]);
  if (!data) return <div style={muted}>Loading linked records…</div>;
  const docs = data.documents || [];
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '8px' }}>
        <div style={muted}>Effective CV protocols and reports. A protocol becomes a mandatory training course and goes to the LMS; completions come back by webhook or pull. Documents go to the DMS when DMS push is on.</div>
        <ActionButton label="Run linked sync now" icon={<FaSyncAlt />} run={async () => { const r = await integrationsAPI.runLinked(); load(); onRan?.(); return r; }}
          title="Send pending / failed hand-offs, pull training completions, push pending deviations" />
      </div>
      {docs.length ? (
        <div className="table-responsive">
          <table className="table" style={{ fontSize: 'var(--fs-sm)' }}>
            <thead><tr><th>Document</th><th>Type</th><th>Plan</th><th>Effective</th><th>LMS training</th><th>Completions</th><th>DMS</th></tr></thead>
            <tbody>
              {docs.map((d) => (
                <tr key={d.doc_id}>
                  <td><strong>{d.document_number}</strong> v{String(d.version).padStart(2, '0')}<div style={{ ...muted, fontSize: 'var(--fs-xs)' }}>{d.title}</div></td>
                  <td>{d.doc_type === 'protocol' ? 'Protocol' : 'Report'} · {d.study_type}</td>
                  <td>{d.plan_code}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>{fmt(d.effective_at)}</td>
                  <td title={d.lms_error || ''}>
                    {d.doc_type === 'protocol'
                      ? <><span className={`badge ${syncTone(d.lms_status)}`}>{d.lms_status || 'not sent'}</span>{d.lms_synced_at && <div style={{ ...muted, fontSize: 'var(--fs-xs)' }}>{fmt(d.lms_synced_at)}</div>}</>
                      : '—'}
                  </td>
                  <td>{d.doc_type === 'protocol' ? `${d.training_records.total} recorded (${d.training_records.from_lms} from LMS, ${d.training_records.pending_qa} awaiting QA)` : '—'}</td>
                  <td title={d.dms_error || ''}><span className={`badge ${syncTone(d.dms_status)}`}>{d.dms_status || 'not sent'}</span>{d.dms_reference && <div style={{ ...muted, fontSize: 'var(--fs-xs)' }}>Ref {d.dms_reference}</div>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : <div style={muted}>No effective CV protocol or report yet (Documents → CV Protocols &amp; Reports).</div>}
      {(data.deviations || []).length > 0 && (
        <div style={{ ...muted, marginTop: '8px' }}>
          Deviations → QMS: {data.deviations.map((d) => `${d.status.replace('_', ' ')}: ${d.count}`).join(' · ')}
        </div>
      )}
    </div>
  );
};

// Per-app explanation: what PrimeClean calls, what the other app calls, and how it flows.
const AppGuide = ({ item }) => {
  const s = item.settings || {};
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const base = s.base_url || `{${item.label} URL}`;
  if (item.key === 'aiventra_lms') {
    const statusPath = (s.employee_path || '').replace(/\/employees(\?.*)?$/, '/training-status');
    return (
      <>
        <div style={card}>
          <h4 style={{ marginTop: 0 }}>Integration endpoints</h4>
          <Endpoint title="Employee list (PrimeClean calls the LMS)">{`GET ${base}${s.employee_path || ''}\nHeader: ${s.api_key_header || 'x-api-key'}: <API key>`}</Endpoint>
          <Endpoint title="Training hand-off (PrimeClean calls the LMS)">{`POST ${base}${s.webhook_path || ''}\n{ "event": "document_effective" | "training_course_approved" | "procedure_completed", "data": { … } }`}</Endpoint>
          <Endpoint title="Training status (PrimeClean pulls from the LMS)">{`GET ${base}${statusPath}?sourceId=<training id>`}</Endpoint>
          <Endpoint title="Completion webhook (the LMS calls PrimeClean)" note="The LMS authenticates with the same API key set on the left.">{`POST ${origin}/api/training/lms-webhook\nHeader: x-api-key: <same API key>\n{ "event": "training_completed", "data": { "employeeId", "procedureId", "completedAt", "trainerName" } }`}</Endpoint>
        </div>
        <div style={{ ...card, marginTop: '12px' }}>
          <h4 style={{ marginTop: 0 }}>How linked syncing works</h4>
          <Steps items={[
            'Users → Match Employees links each PrimeClean user to an LMS employee ID (employee sync fills designation too).',
            'When a CV protocol is made effective, PrimeClean creates the mandatory training course and sends document_effective; the LMS creates the training and assigns the departments that sign cleaning, sampling, visual and testing.',
            'Completions come back through the webhook, or are pulled from the LMS training-status endpoint; each lands as a training record awaiting QA approval.',
            'Approved manual training records are pushed to the LMS.',
            'With "Automatic linked sync" on, failed hand-offs are retried and completions pulled on the interval set on the left — no button presses needed.'
          ]} />
        </div>
      </>
    );
  }
  if (item.key === 'aiventra_dms') {
    return (
      <>
        <div style={card}>
          <h4 style={{ marginTop: 0 }}>Integration endpoint</h4>
          <Endpoint title="Document hand-off (PrimeClean calls the DMS)" note="The DMS answers 200 with an optional { reference } that PrimeClean stores against the document. Your DMS must expose this endpoint; Veeva / MasterControl / SharePoint need a small adapter that accepts this JSON.">
            {`POST ${base}${s.webhook_path || ''}\nHeader: ${s.api_key_header || 'x-api-key'}: <API key>\n{ "event": "document_effective", "source": "AIVENTRA_CLEAN", "data": {\n    "documentNumber", "title", "version", "documentType": "CV_PROTOCOL" | "CV_REPORT",\n    "studyType", "planCode", "effectiveDate", "effectiveBy", "approvedBy", "approvedAt",\n    "supersedesVersion" } }`}
          </Endpoint>
        </div>
        <div style={{ ...card, marginTop: '12px' }}>
          <h4 style={{ marginTop: 0 }}>How it works</h4>
          <Steps items={[
            'A CV protocol or report is approved and made effective in Documents.',
            'PrimeClean sends it to the DMS as a controlled document and keeps the DMS reference.',
            'A failed hand-off is retried by "Run linked sync now", by the automatic sync, or by "Send to DMS" on the document.'
          ]} />
        </div>
      </>
    );
  }
  if (item.key === 'aiventra_qms') {
    return (
      <>
        <div style={card}>
          <h4 style={{ marginTop: 0 }}>Integration endpoint</h4>
          <Endpoint title="Deviation hand-off (PrimeClean calls the QMS)">
            {`POST ${base}${s.webhook_path || ''}\n{ "event": "deviation_opened", "source": "AIVENTRA_CLEAN", "data": {\n    "deviationId", "deviationType", "resultId", "arNo", "batchNumber",\n    "criterion", "resultValue", "limitValue", "openedAt" } }`}
          </Endpoint>
          <ActionButton label="Push pending / failed deviations" icon={<FaSyncAlt />} run={() => integrationsAPI.retryDeviations()} disabled={!(item.purchased && item.enabled)} />
        </div>
        <div style={{ ...card, marginTop: '12px' }}>
          <h4 style={{ marginTop: 0 }}>How it works</h4>
          <Steps items={[
            'A failed result (OOS) raises a deviation in PrimeClean.',
            'The deviation is sent to the QMS for investigation and CAPA; the QMS reference is stored on it.',
            'A QMS outage never blocks recording the result — failed pushes are retried from here or by the linked sync.'
          ]} />
        </div>
      </>
    );
  }
  return (
    <>
      <div style={card}>
        <h4 style={{ marginTop: 0 }}>Integration endpoints (PrimeClean calls {item.label})</h4>
        {'employee_sync' in item.feature_labels && <Endpoint title="Employees">{`GET ${base}${s.employee_path || ''}`}</Endpoint>}
        {'product_sync' in item.feature_labels && <Endpoint title="Products (finished goods)">{`GET ${base}${s.product_path || ''}`}</Endpoint>}
        {'api_sync' in item.feature_labels && <Endpoint title="APIs (active substances)">{`GET ${base}${s.api_path || ''}`}</Endpoint>}
      </div>
      <div style={{ ...card, marginTop: '12px' }}>
        <h4 style={{ marginTop: 0 }}>How it works</h4>
        <Steps items={[
          'Employee sync links external employee IDs and designations to existing users — it never creates accounts.',
          'Product / API sync fetches records for the normal import on Product Master / API Master; every record still needs e-signature and Product → QA approval.',
          'Field mapping (Show paths & field mapping) maps the source fields to PrimeClean fields.'
        ]} />
      </div>
    </>
  );
};

const AppTab = ({ item, onSaved, refreshKey, bump }) => {
  const active = item.purchased && item.enabled;
  const hasEmployees = 'employee_sync' in item.feature_labels;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 7fr) minmax(0, 5fr)', gap: '14px', alignItems: 'start' }} className="integration-hub-grid">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <IntegrationCard item={item} onSaved={(u) => { onSaved(u); bump(); }} />
          {hasEmployees && (
            <div style={{ ...card, display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
              <strong style={{ marginRight: 'auto' }}>Sync now</strong>
              <ActionButton label="Sync employees" icon={<FaUsers />} disabled={!active || !item.features.employee_sync}
                run={async () => { const r = await integrationsAPI.syncEmployees(item.key); bump(); return r; }} />
              <ActionButton label="Sync departments" icon={<FaBuilding />} disabled={!active}
                run={async () => { const r = await integrationsAPI.syncDepartments(item.key); bump(); return r; }} />
            </div>
          )}
        </div>
        <div><AppGuide item={item} /></div>
      </div>
      <div style={card}>
        <h4 style={{ marginTop: 0 }}>Sync history — {item.label}</h4>
        <SyncHistory integrationKey={item.key} refreshKey={refreshKey} />
      </div>
    </div>
  );
};

const Overview = ({ items, open, refreshKey, bump }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
    <div className="form-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))' }}>
      {items.map((item) => {
        const ok = item.last_test_status?.startsWith('OK');
        return (
          <button key={item.key} type="button" onClick={() => open(item.key)} style={{ ...card, textAlign: 'left', cursor: 'pointer', borderLeft: `4px solid ${item.purchased && item.enabled ? 'var(--primary-color)' : 'var(--border-color)'}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '6px', alignItems: 'center' }}>
              <strong>{item.label}</strong>{statusBadge(item)}
            </div>
            <div style={muted}>{item.category}</div>
            <div style={{ ...muted, fontSize: 'var(--fs-xs)', marginTop: '6px' }}>
              {item.last_test_status
                ? <span style={{ color: ok ? 'var(--success-color, #16a34a)' : 'var(--danger-color, #dc2626)' }}>{ok ? <FaCheckCircle /> : <FaTimesCircle />} {item.last_test_status}</span>
                : 'Not tested yet'}
            </div>
            <div style={{ ...muted, fontSize: 'var(--fs-xs)' }}>Last sync: {fmt(item.last_sync_at)}</div>
          </button>
        );
      })}
    </div>
    <div style={card}>
      <h4 style={{ marginTop: 0 }}>Linked records</h4>
      <LinkedRecords refreshKey={refreshKey} onRan={bump} />
    </div>
  </div>
);

const TAB_ORDER = ['aiventra_lms', 'aiventra_dms', 'aiventra_qms', 'sap', 'custom_app'];

const IntegrationHub = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const tab = new URLSearchParams(location.search).get('tab') || 'overview';
  const bump = () => setRefreshKey((k) => k + 1);

  const load = useCallback(async () => {
    try {
      const res = await integrationsAPI.list();
      const list = Array.isArray(res?.data?.data) ? res.data.data : [];
      setItems(list.sort((a, b) => TAB_ORDER.indexOf(a.key) - TAB_ORDER.indexOf(b.key)));
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to load integrations');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const open = (key) => { navigate(`/integrations?tab=${key}`, { replace: true }); window.scrollTo(0, 0); };
  const handleSaved = (updated) => {
    if (updated?.key) setItems((prev) => prev.map((it) => (it.key === updated.key ? updated : it)));
    else load();
  };

  const tabs = [
    { key: 'overview', label: 'Overview & Linked Records' },
    ...items.map((it) => ({ key: it.key, label: it.label })),
    { key: 'history', label: 'Sync History' }
  ];
  const current = items.find((it) => it.key === tab);

  return (
    <div>
      <div style={{ marginBottom: '12px' }}>
        <h2 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><FaPlug /> Integration Hub</h2>
        <p className="page-subtitle">Connect PrimeClean with your LMS, DMS, QMS, SAP and other systems — connection, linked syncing and history in one place. Which add-ons this client bought is set in Commercial Setup.</p>
      </div>
      <div className="lms-tabs" style={{ marginBottom: '16px', overflowX: 'auto', whiteSpace: 'nowrap' }}>
        {tabs.map((t) => (
          <button key={t.key} type="button" className={`lms-tab ${tab === t.key ? 'active' : ''}`} onClick={() => open(t.key)}>{t.label}</button>
        ))}
      </div>
      {loading ? <div style={muted}>Loading integrations…</div> : (
        <>
          {tab === 'overview' && <Overview items={items} open={open} refreshKey={refreshKey} bump={() => { bump(); load(); }} />}
          {tab === 'history' && <div style={card}><SyncHistory refreshKey={refreshKey} limit={200} /></div>}
          {current && <AppTab key={current.key} item={current} onSaved={handleSaved} refreshKey={refreshKey} bump={() => { bump(); load(); }} />}
          {!current && !['overview', 'history'].includes(tab) && (
            <div style={muted}><FaExclamationTriangle /> Unknown integration.</div>
          )}
        </>
      )}
    </div>
  );
};

export default IntegrationHub;
